import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { TextField } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Popover from '@mui/material/Popover';
import Select from '@mui/material/Select';
import Tab from '@mui/material/Tab';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { deriveHeroFrom, deriveHeroTo } from 'app/theme';

export const ReportsContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100%',
});

// ─── Hero banner ──────────────────────────────────────────────────────────────

export const ReportsHeroRow = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  marginBottom: 20,
});

export const ReportsHeroTextBox = styled(Box)({ minWidth: 0 });

export const ReportsHeroTitle = styled('h1')({
  margin: 0,
  fontSize: 22,
  fontWeight: 700,
  color: '#fff',
  lineHeight: 1.3,
});

export const ReportsHeroSubtitle = styled('p')({
  margin: '4px 0 0',
  fontSize: 13,
  color: 'rgba(255,255,255,0.7)',
});

export const ReportsHeroActions = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexShrink: 0,
  marginLeft: 16,
});

export const ReportsHeroExportButton = styled(Button)({
  textTransform: 'none',
  fontSize: 13,
  fontWeight: 500,
  borderRadius: '8px',
  backgroundColor: '#fff',
  color: '#000000',
  border: '1.5px solid #e0daf7',
  gap: 6,
  padding: '6px 14px',
  '&:hover': { backgroundColor: '#f3f0ff', borderColor: '#c4b8f5' },
});

export const ReportsHeroStatCards = styled(Box)({
  display: 'flex',
  gap: 16,
  width: '100%',
});

export const ReportsHeroStatCard = styled(Box)({
  flex: '1 1 0',
  backgroundColor: 'rgba(255,255,255,0.10)',
  borderRadius: 12,
  padding: '14px 20px',
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  minWidth: 0,
});

export const ReportsHeroStatLabel = styled('span')({
  fontSize: 11,
  fontWeight: 600,
  color: 'rgba(255,255,255,0.6)',
  textTransform: 'uppercase',
  letterSpacing: '0.6px',
});

export const ReportsHeroStatValue = styled('span')({
  fontSize: 26,
  fontWeight: 700,
  color: '#fff',
  fontFamily: 'monospace',
  letterSpacing: 1,
  lineHeight: 1.2,
});

export const ReportsHeroStatSub = styled('span')({
  fontSize: 12,
  color: 'rgba(255,255,255,0.55)',
});

// ─── Tabs (below hero) ────────────────────────────────────────────────────────

export const TabsHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(1),
  flexShrink: 0,
}));

export const TabsWrapper = styled(Tabs)(({ theme }) => ({
  width: 'fit-content',
  minHeight: 0,
  '& .MuiTabs-indicator': { display: 'none' },
  '& .MuiTabs-flexContainer': { gap: 2 },
  '& .MuiTabs-root': { overflow: 'visible' },
  '& .MuiTabs-scroller': { overflow: 'visible !important', padding: '4px 4px' },
}));

export const TabLabel = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontSize: 14,
  fontWeight: 500,
  minHeight: 36,
  padding: '6px 16px',
  borderRadius: 20,
  border: '1.5px solid transparent',
  color: theme.palette.text.secondary,
  transition: 'all 0.2s ease',
  '&.Mui-selected': {
    color: theme.palette.text.primary,
    fontWeight: 700,
    border: '1.5px solid transparent',
    backgroundColor: '#fff',
    boxShadow: '0 1px 4px rgba(34,28,90,0.10), 0 2px 8px rgba(34,28,90,0.06)',
  },
}));

export const DateRangeButton = styled(Button)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  textTransform: 'none',
  fontSize: '0.95rem',
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

export const FiltersRow = styled(Box)({
  marginBottom: 16,
});

export const FiltersSection = styled(Paper)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: theme.spacing(1),
  borderRadius: 14,
  boxShadow: '0 1px 2px rgba(34,28,90,0.04), 0 8px 24px -8px rgba(34,28,90,0.06)',
  padding: theme.spacing(1.5, 2),
}));

export const FilterSelect = styled(Select)(({ theme }) => ({
  height: 40,
  minWidth: 140,
  fontSize: '0.95rem',
  backgroundColor: theme.palette.background.paper,
  '& .MuiOutlinedInput-notchedOutline': {
    // borderColor: theme.palette.divider,
    border: 'none',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.text.secondary,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
  },
}));

export const FilterActionsContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginLeft: 'auto',
  flexShrink: 0,
});

export const ApplyButton = styled(Button)(({ theme }) => ({
  height: 32,
  fontWeight: 600,
  fontSize: 12,
  borderRadius: 10,
  flexShrink: 0,
  paddingLeft: 12,
  paddingRight: 12,
  textTransform: 'none',
  background: `linear-gradient(135deg, ${deriveHeroFrom(theme.palette.primary.main)} 0%, ${theme.palette.primary.main} 50%, ${deriveHeroTo(theme.palette.primary.main)} 100%)`,
  '&:hover': {
    background: `linear-gradient(135deg, ${deriveHeroFrom(theme.palette.primary.main)} 0%, ${deriveHeroFrom(theme.palette.primary.main)} 50%, ${theme.palette.primary.main} 100%)`,
  },
  '&.Mui-disabled': {
    color: '#fff',
    background: theme.palette.action.disabledBackground,
  },
}));

export const ClearFiltersButton = styled('button')(({ theme }) => ({
  flexShrink: 0,
  border: 'none',
  background: 'none',
  padding: 0,
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: '0.75rem',
  fontWeight: 500,
  whiteSpace: 'nowrap',
  color: theme.palette.text.disabled,
  transition: 'color 0.15s ease',
  '&:hover': { color: theme.palette.text.primary },
}));

export const ContentSection = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

export const TabContentWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
}));

export const DatePickerIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  padding: theme.spacing(0.5),
}));

// Filter menu styles
export const filterMenuPaperStyles = {
  '& .MuiMenuItem-root': {
    fontSize: '0.85rem',
    minHeight: 32,
    p: 0,
  },
};

export const filterMenuPaperWithPaddingStyles = {
  '& .MuiMenuItem-root': {
    fontSize: '0.85rem',
    minHeight: 32,
    py: 0.5,
  },
};

export const FilterCheckbox = styled(Checkbox)(({ theme }) => ({
  mr: theme.spacing(1),
}));

export const FilterDropdownButton = styled('button')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: 40,
  minWidth: 82,
  padding: theme.spacing(0, 1.75),
  fontSize: 12,
  fontWeight: 500,
  fontFamily: 'inherit',
  color: '#5A5578',
  backgroundColor: 'rgba(248, 246, 253, 0.6)',
  border: '1px solid rgba(34, 28, 90, 0.10)',
  borderRadius: 10,
  cursor: 'pointer',
  gap: theme.spacing(0.5),
  flexShrink: 0,
  transition: theme.transitions.create(['border-color', 'background-color'], {
    duration: theme.transitions.duration.short,
  }),
  '&:hover': {
    backgroundColor: 'rgba(248, 246, 253, 0.9)',
    borderColor: 'rgba(34, 28, 90, 0.20)',
  },
}));

export const FilterDropdownPaper = styled(Paper)(({ theme }) => ({
  minWidth: 200,
  padding: theme.spacing(1),
}));

export const FilterOptionRow = styled('label', {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})<{ isSelected?: boolean }>(({ theme, isSelected }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(0.75, 1),
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: '0.75rem',
  fontWeight: isSelected ? 500 : 400,
  userSelect: 'none' as const,
  color: isSelected ? theme.palette.primary.main : theme.palette.text.primary,
  backgroundColor: isSelected ? `${theme.palette.primary.main}1A` : 'transparent',
  transition: theme.transitions.create('background-color', {
    duration: theme.transitions.duration.shortest,
  }),
  '&:hover': {
    backgroundColor: isSelected ? `${theme.palette.primary.main}26` : theme.palette.action.hover,
  },
}));

export const FilterMiniCheckbox = styled('span', {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})<{ isSelected?: boolean }>(({ theme, isSelected }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: 14,
  height: 14,
  borderRadius: 4,
  border: `1px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`,
  backgroundColor: isSelected ? theme.palette.primary.main : 'transparent',
  color: '#fff',
  fontSize: 9,
  fontWeight: 700,
  lineHeight: 1,
  transition: theme.transitions.create(['background-color', 'border-color'], {
    duration: theme.transitions.duration.shortest,
  }),
}));

export const FilterSectionLabel = styled('span')(({ theme }) => ({
  display: 'block',
  fontSize: '0.65rem',
  fontWeight: 700,
  letterSpacing: '0.06em',
  color: theme.palette.text.secondary,
  textTransform: 'uppercase' as const,
  padding: theme.spacing(0, 1, 0.5),
}));

export const FilterSearchInput = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    fontSize: '0.85rem',
  },
}));

export const MenuItemText = styled('span')(({ theme }) => ({
  fontSize: '0.85rem',
}));

// Calendar section styles
export const CalendarSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  ml: 'auto',
  pr: theme.spacing(1),
}));

export const CalendarDateButton = styled(Box)<{ component: string }>(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  px: theme.spacing(1.5),
  py: theme.spacing(0.75),
  border: 'none',
  backgroundColor: 'transparent',
  cursor: 'pointer',
  borderRadius: theme.spacing(1),
  color: theme.palette.text.primary,
  fontSize: '0.95rem',
  fontWeight: 500,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

export const CalendarPopoverContent = styled(Box)(({ theme }) => ({
  // p: theme.spacing(2),
  display: 'flex',
  flexDirection: 'row',
  // gap: theme.spacing(2),
}));

// Clear filters container
export const ClearFiltersContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
}));

// Summary table styles
export const SummaryTitleCell = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

export const SummaryEntryCount = styled('span')(({ theme }) => ({
  cursor: 'pointer',
  userSelect: 'none',
  color: theme.palette.text.secondary,
  fontSize: '0.85rem',
  marginRight: theme.spacing(0.5),
  backgroundColor: theme.palette.grey[200],
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(0.25, 0.5),
  minWidth: 24,
  textAlign: 'center',
  fontWeight: 500,
}));

export const SummaryColorDot = styled(Box)<{ dotcolor: string }>(({ dotcolor, theme }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: dotcolor,
  flexShrink: 0,
}));

export const SummaryProjectName = styled('span')(({ theme }) => ({
  fontWeight: 500,
  fontSize: '0.8rem',
}));

export const SummaryClientName = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.85rem',
}));

export const SummaryDuration = styled('span')(({ theme }) => ({
  fontFamily: 'monospace',
  fontSize: '0.9rem',
}));

export const SummaryPercentage = styled('span')(({ theme }) => ({
  fontSize: '0.9rem',
  fontWeight: 500,
}));

export const SummaryBillingIcon = styled(Box)<{ isbillable: boolean }>(({ isbillable, theme }) => ({
  color: isbillable ? theme.palette.secondary.main : theme.palette.text.disabled,
}));

export const SummaryAmount = styled('span')(({ theme }) => ({
  fontFamily: 'monospace',
  fontSize: '0.9rem',
  color: theme.palette.text.primary,
}));

// Group by bar styles
export const GroupByBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1.25, 2),
  backgroundColor: theme.palette.background.paper,
  borderRadius: 14,
  boxShadow: '0 1px 2px rgba(34,28,90,0.04), 0 8px 24px -8px rgba(34,28,90,0.06)',
  marginBottom: theme.spacing(1.5),
}));

export const GroupByLabel = styled('span')(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.text.secondary,
  fontWeight: 500,
  flexShrink: 0,
}));

export const GroupByChip = styled('button')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(0.5),
  fontSize: 12,
  fontWeight: 500,
  height: 34,
  minWidth: 110,
  padding: theme.spacing(0, 1.25),
  borderRadius: 10,
  border: '1px solid rgba(34, 28, 90, 0.10)',
  backgroundColor: 'rgba(248, 246, 253, 0.6)',
  color: '#5A5578',
  cursor: 'pointer',
  fontFamily: 'inherit',
  transition: 'background-color 0.15s ease, border-color 0.15s ease',
  '&:hover': {
    backgroundColor: 'rgba(248, 246, 253, 0.9)',
    borderColor: 'rgba(34, 28, 90, 0.20)',
  },
}));

export const AddGroupButton = styled('button')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  fontSize: '1rem',
  color: theme.palette.text.secondary,
  border: `1px dashed ${theme.palette.divider}`,
  borderRadius: 4,
  backgroundColor: 'transparent',
  cursor: 'pointer',
  fontFamily: 'inherit',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

// Summary tree sub-group row styles
export const SubGroupRow = styled(TableRow, {
  shouldForwardProp: (prop) => prop !== 'depth',
})<{ depth?: number }>(({ theme, depth = 1 }) => ({
  backgroundColor: depth === 1 ? theme.palette.grey[50] : theme.palette.grey[100],
}));

export const SubGroupTitleCell = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'depth',
})<{ depth?: number }>(({ theme, depth = 1 }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  paddingLeft: theme.spacing(depth * 3),
}));

export const SubGroupCount = styled('span')(({ theme }) => ({
  cursor: 'pointer',
  userSelect: 'none',
  color: theme.palette.text.secondary,
  fontSize: '0.8rem',
  backgroundColor: theme.palette.grey[200],
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(0.25, 0.5),
  minWidth: 20,
  textAlign: 'center',
  fontWeight: 500,
  flexShrink: 0,
}));

export const SubGroupName = styled('span')(({ theme }) => ({
  fontSize: '0.78rem',
  color: theme.palette.text.primary,
}));

export const SubGroupDuration = styled('span')(({ theme }) => ({
  fontFamily: 'monospace',
  fontSize: '0.78rem',
  color: theme.palette.text.secondary,
}));

export const SubGroupAmount = styled('span')(({ theme }) => ({
  fontFamily: 'monospace',
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
}));

// Date range shortcuts styles
export const DateShortcutsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'wrap',
  gap: theme.spacing(0.5),
  marginBottom: theme.spacing(1),
  // paddingTop: theme.spacing(4),
}));

export const DateShortcutButton = styled(Button)(({ theme }) => ({
  fontSize: '0.75rem',
  padding: theme.spacing(0.5, 1),
  minWidth: 'auto',
  height: 28,
  textTransform: 'none',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  backgroundColor: theme.palette.action.hover,
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
  },
}));

// Compact calendar styles
export const CompactCalendarPopover = styled(Popover)(({ theme }) => ({
  '& .MuiPaper-root': {
    padding: theme.spacing(1),
  },
}));

export const CompactCalendarContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
}));

export const CompactCalendar = styled(Box)<{ startdate?: string; enddate?: string }>(
  ({ theme, startdate, enddate }) => ({
    '& .MuiPickersCalendarHeader-label': {
      fontSize: '0.85rem',
    },
    '& .MuiPickersDay-root': {
      fontSize: '0.85rem',
    },
    '& .MuiDateCalendar-root': {
      width: '100%',
      height: 'fit-content',
    },
    '& .MuiPickersDay-root.Mui-selected': {
      backgroundColor: theme.palette.primary.main,
    },
    '& .MuiPickersDay-root[aria-selected="true"]': {
      backgroundColor: theme.palette.primary.main,
    },
    // Fix calendar height so it doesn't jump when switching months
    '& .MuiDayCalendar-slideTransition': {
      minHeight: 250,
    },
    '& .MuiPickersSlideTransition-root': {
      minHeight: 250,
    },
  }),
);

// Export actions styles
export const ExportActionsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  marginLeft: 'auto',
  justifyContent: 'flex-end',
}));

export const ExportButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontSize: '0.875rem',
  fontWeight: 500,
  padding: theme.spacing(0.5, 1.5),
  gap: theme.spacing(0.5),
  backgroundColor: theme.palette.action.hover,
  color: theme.palette.text.primary,
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
  },
}));

export const ExportMenu = styled(MenuItem)(({ theme }) => ({
  fontSize: 13,
  fontWeight: 500,
  minHeight: 36,
  gap: 8,
  px: 1.5,
}));

export const ExportMenuIcon = styled('span')({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
});

// Calendar icon styling
export const CalendarIcon = styled('span')(({ theme }) => ({
  fontSize: 18,
  display: 'flex',
  alignItems: 'center',
}));

// Styled PickersDay for date range highlighting
export const RangePickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) => prop !== 'isselected' && prop !== 'isinrange',
})<{ isselected?: boolean; isinrange?: boolean }>(({ theme, isselected, isinrange }) => ({
  backgroundColor: isselected
    ? theme.palette.primary.main
    : isinrange
      ? theme.palette.primary.light
      : 'transparent',
  color: isselected || isinrange ? theme.palette.primary.contrastText : theme.palette.text.primary,
  '&:hover': {
    backgroundColor: isselected
      ? theme.palette.primary.dark
      : isinrange
        ? theme.palette.primary.main
        : theme.palette.action.hover,
  },
}));

// Detailed report table styles
export const DetailedTitleCell = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
  minHeight: 36,
}));

export const DetailedDescription = styled('span')(({ theme }) => ({
  fontSize: '0.78rem',
  cursor: 'pointer',
  padding: '2px 4px',
  borderRadius: 4,
  lineHeight: 1.4,
  flex: 1,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

export const DetailedDescriptionInput = styled(TextField)(({ theme }) => ({
  flex: 1,
  '& .MuiInput-input': {
    fontSize: '0.78rem',
    padding: '2px 4px',
    lineHeight: 1.4,
  },
  '& .MuiInput-underline:before': {
    borderBottomColor: theme.palette.divider,
  },
  '& .MuiInput-underline:after': {
    borderBottomColor: theme.palette.primary.main,
  },
}));

export const DetailedProjectName = styled('span')<{ color?: string }>(({ color, theme }) => ({
  fontWeight: 500,
  fontSize: '0.75rem',
  color: color || theme.palette.text.secondary,
}));

export const DetailedClientName = styled('span')(({ theme }) => ({
  fontSize: '0.72rem',
  color: theme.palette.text.disabled,
}));

export const DetailedUserName = styled('span')(({ theme }) => ({
  fontSize: '0.78rem',
}));

export const DetailedAmount = styled('span')(({ theme }) => ({
  fontFamily: 'monospace',
  fontSize: '0.9rem',
  fontWeight: 500,
}));

export const DetailedDuration = styled('span')(({ theme }) => ({
  fontFamily: 'monospace',
  fontSize: '0.78rem',
}));

export const DetailedTimeRange = styled('span')(({ theme }) => ({
  fontSize: '0.78rem',
  fontFamily: 'monospace',
  cursor: 'pointer',
  color: theme.palette.text.primary,
  borderRadius: 8,
  padding: '3px 8px',
  border: `1px solid transparent`,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.divider,
  },
}));

export const DetailedTimeEditContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 8,
  padding: '2px 6px',
  '&:focus-within': {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 2px ${theme.palette.primary.main}22`,
  },
}));

export const DetailedTimeInput = styled('input')(({ theme }) => ({
  fontSize: '0.78rem',
  fontFamily: 'monospace',
  padding: '2px 0',
  border: 'none',
  borderRadius: 0,
  width: '38px',
  textAlign: 'center',
  outline: 'none',
  background: 'transparent',
  color: theme.palette.text.primary,
}));

export const DetailedTimeSeparator = styled('span')(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.disabled,
  userSelect: 'none',
}));

// Report totals section
export const ReportTotalsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(3),
  padding: theme.spacing(1.5),
  paddingRight: theme.spacing(1),
  marginTop: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  // borderRight: `1px solid ${theme.palette.divider}`,
  borderTopRightRadius: theme.shape.borderRadius,
  borderTopLeftRadius: theme.shape.borderRadius,
}));

export const ReportTotalItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  // flexDirection: 'column',
  gap: theme.spacing(0.25),
  alignItems: 'center',
}));

export const ReportTotalLabel = styled('span')(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  // textTransform: 'uppercase',
  fontWeight: 600,
  letterSpacing: '0.5px',
}));

export const ReportTotalValue = styled('span')(({ theme }) => ({
  fontSize: '1.1rem',
  fontWeight: 600,
  fontFamily: 'monospace',
  color: theme.palette.secondary.main,
}));

// Detailed tab additional styles
export const DetailedTimeCellContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

export const DetailedDateButton = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'as',
})<{ as?: string }>(({ theme }) => ({
  cursor: 'pointer',
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  borderRadius: 8,
  padding: '3px 8px',
  border: `1px solid transparent`,
  whiteSpace: 'nowrap',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.divider,
  },
}));

export const DetailedActionIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'as',
})<{ as?: string }>(({ theme }) => ({
  cursor: 'pointer',
}));

export const DetailedDeleteIcon = styled(DeleteIcon)({
  fontSize: '14px !important',
});

export const DetailedProjectClientRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

// Summary and Weekly tab additional styles
export const ExportMenuPaper = styled(Paper)(({ theme }) => ({
  minWidth: 100,
}));

export const WeeklyDurationValue = styled(Box)(({ theme }) => ({
  fontFamily: 'monospace',
  fontSize: '0.85rem',
}));

export const WeeklyEmptyValue = styled(Box)(({ theme }) => ({
  color: theme.palette.text.disabled,
}));

export const WeeklyTotalValue = styled(Box)(({ theme }) => ({
  fontFamily: 'monospace',
  fontSize: '0.9rem',
  fontWeight: 600,
}));

// Donut chart styles
export const donutTooltipSx = {
  bgcolor: '#222',
  color: '#fff',
  borderRadius: 1,
  px: 1.5,
  py: 0.75,
  fontSize: 12,
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  boxShadow: 4,
} as const;

// Chart card styles
export const chartCardSx = {
  bgcolor: '#fff',
  borderRadius: 2,
  p: 3,
  boxShadow: '0 1px 2px rgba(34,28,90,0.04), 0 8px 24px -8px rgba(34,28,90,0.06)',
} as const;

export const chartModeDropdownSx = {
  fontSize: 12,
  textTransform: 'none' as const,
  borderRadius: '20px',
  borderColor: 'divider',
  color: 'text.secondary',
  px: 1.5,
  py: 0.4,
  '& .MuiButton-endIcon': { ml: 0.25 },
} as const;

export const DonutChartCard = styled(Box)(({ theme }) => ({
  flex: '0 0 34%',
  minWidth: 260,
  maxWidth: 340,
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius * 2,
  padding: '16px 20px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
}));

// ── ReportsPage ───────────────────────────────────────────────────────────────

export const AddEntryButton = styled(Button)({
  textTransform: 'none',
  fontWeight: 500,
  borderRadius: '8px',
  fontSize: 13,
  gap: 6,
  padding: '6px 14px',
  backgroundColor: '#fff',
  color: '#000',
  border: '1.5px solid #e0daf7',
  '&:hover': { backgroundColor: '#f3f0ff', borderColor: '#c4b8f5' },
});

// ── SmartReportWidget ─────────────────────────────────────────────────────────

export const SmartWidgetPaperSx = {
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  gap: 2.5,
  px: 2,
  py: 1,
  mb: 1.5,
  borderRadius: '14px',
  boxShadow: '0 1px 2px rgba(34,28,90,0.04), 0 8px 24px -8px rgba(34,28,90,0.06)',
  border: 'none',
} as const;

export const SmartWidgetTitleBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  flexShrink: 0,
});

export const SmartWidgetDivider = styled(Box)({
  width: 1,
  height: 18,
  flexShrink: 0,
});

export const SmartWidgetCountsBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 20,
  flexGrow: 1,
});

export const SmartWidgetCountItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 5,
});

export const SmartWidgetDot = styled(Box)({
  width: 7,
  height: 7,
  borderRadius: '50%',
  flexShrink: 0,
});

export const SmartWidgetLoadingBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

export const smartWidgetViewButtonSx = {
  flexShrink: 0,
  fontSize: 12,
  textTransform: 'none',
  borderRadius: '10px',
  borderColor: 'divider',
  color: 'text.secondary',
  px: 1.5,
  py: 0.5,
  '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
} as const;

// ── SummaryDonutChart ─────────────────────────────────────────────────────────

export const DonutChartWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
});

export const DonutLegendBox = styled(Box)({
  width: '100%',
  marginTop: 8,
  maxHeight: 232,
  overflowY: 'auto',
  scrollbarWidth: 'none' as const,
});

export const DonutLegendRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingTop: 5,
  paddingBottom: 5,
});

export const DonutLegendLeft = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  minWidth: 0,
});

export const DonutLegendDot = styled(Box)({
  width: 10,
  height: 10,
  borderRadius: '50%',
  flexShrink: 0,
});

export const donutCenterTimeSvgStyle = {
  fontSize: 15,
  fontWeight: 700,
  fontFamily: 'monospace',
  fill: '#333',
} as const;

export const donutCenterLabelSvgStyle = {
  fontSize: 11,
  fill: '#888',
} as const;

export const donutTooltipDotSx = {
  width: 8,
  height: 8,
  borderRadius: '50%',
  flexShrink: 0,
} as const;

// ── AddTimeEntryModal ─────────────────────────────────────────────────────────

export const addTimeEntryInputSx = {
  '& .MuiInputLabel-root': { fontSize: 13 },
  '& .MuiInputBase-input': { fontSize: 13 },
  '& .MuiOutlinedInput-root': { borderRadius: '10px' },
  '& .MuiOutlinedInput-notchedOutline legend': { fontSize: '0.6825em' },
} as const;

export const addTimeEntryCancelButtonSx = {
  textTransform: 'none',
  fontSize: 13,
  fontWeight: 500,
  borderRadius: '8px',
  color: 'text.secondary',
} as const;

export const addTimeEntryActionsSx = {
  px: 3,
  pb: 2.5,
  pt: 1.5,
  gap: 1,
} as const;

export const addTimeEntryDialogTitleSx = {
  fontWeight: 700,
  fontSize: 14,
  pb: 1,
  pt: 2.5,
  px: 3,
} as const;

export const addTimeEntryDialogContentSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1.5,
  pt: '8px !important',
  px: 3,
} as const;

export const addTimeEntryProjectRowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
} as const;

export const addTimeEntryTimeRowSx = { display: 'flex', gap: 1.5 } as const;

export const addTimeEntryTimeFieldSx = {
  flex: 1,
  '& .MuiInputLabel-root': { fontSize: 13 },
  '& .MuiInputBase-input': { fontSize: 13 },
  '& .MuiOutlinedInput-root': { borderRadius: '10px' },
  '& .MuiOutlinedInput-notchedOutline legend': { fontSize: '0.6825em' },
} as const;

export const addTimeEntrySubmitButtonSx = {
  textTransform: 'none',
  fontSize: 13,
  fontWeight: 600,
  borderRadius: '8px',
  px: 2.5,
  boxShadow: 'none',
  background: 'linear-gradient(135deg, #7c6fe0 0%, #6e4ff7 50%, #5a3de8 100%)',
  '&:hover': {
    background: 'linear-gradient(135deg, #7c6fe0 0%, #7c6fe0 50%, #6e4ff7 100%)',
    boxShadow: 'none',
  },
  '&.Mui-disabled': { background: 'rgba(0,0,0,0.12)', color: 'rgba(0,0,0,0.26)' },
} as const;

// ── SummaryBarChart ───────────────────────────────────────────────────────────

export const ChartHeaderRow = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  marginBottom: 12,
});

export const ChartTitle = styled(Box)({
  fontWeight: 700,
  fontSize: 14,
  lineHeight: 1.4,
});

export const ChartHeaderRight = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: 6,
});

export const ChartLegendRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
});

export const ChartLegendItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
});

export const ChartLegendDot = styled(Box)<{ dotcolor?: string }>(({ dotcolor }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: dotcolor,
  flexShrink: 0,
}));

export const chartModeViewBySx = {
  color: 'text.secondary',
  mr: 0.4,
  fontSize: 12,
  fontWeight: 400,
} as const;

export const chartModeValueSx = {
  fontWeight: 600,
  fontSize: 12,
  color: 'text.primary',
} as const;

export const chartMenuItemSx = { fontSize: '0.85rem', minHeight: 32 } as const;

export const ChartOuterBox = styled(Box)({ marginBottom: 8 });

// ── SummaryTab ────────────────────────────────────────────────────────────────

export const SummaryTabRoot = styled(Box)({ display: 'flex', flexDirection: 'column' });

export const SummaryChartWrapper = styled(Box)({ paddingTop: 8, paddingBottom: 8 });

export const SummaryContentRow = styled(Box)({
  display: 'flex',
  gap: 16,
  marginTop: 12,
  alignItems: 'flex-start',
});

export const SummaryTableScrollWrapper = styled(Box)({ flex: 1, minWidth: 0 });

export const DonutChartTitle = styled(Box)({
  fontWeight: 700,
  fontSize: 14,
  marginBottom: 4,
});

export const groupByMenuItemSx = { fontSize: '0.85rem', minHeight: 32 } as const;

export const groupByMenuItemItalicSx = {
  fontSize: '0.85rem',
  minHeight: 32,
  fontStyle: 'italic',
} as const;

// ── WeeklyTab ─────────────────────────────────────────────────────────────────

export const WeeklyTabRoot = styled(Box)({
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

export const WeeklyDateHeaderBox = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'baseline',
  justifyContent: 'flex-end',
  gap: 4,
});

export const WeeklyDateHeaderDay = styled(Box)({
  fontSize: 11,
  fontWeight: 700,
  lineHeight: 1.3,
});

export const WeeklyDateHeaderDate = styled(Box)({
  fontSize: 10,
  lineHeight: 1.3,
  opacity: 0.75,
});

export const WeeklyProjectCountChip = styled(Box)(({ theme }) => ({
  cursor: 'pointer',
  userSelect: 'none' as const,
  fontSize: '0.8rem',
  borderRadius: 4,
  padding: '2px 4px',
  minWidth: 20,
  textAlign: 'center' as const,
  fontWeight: 500,
  flexShrink: 0,
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.grey[200],
}));

export const WeeklyExpandedDurationCell = styled(Box)(({ theme }) => ({
  fontFamily: 'monospace',
  fontSize: '0.8rem',
  color: theme.palette.text.secondary,
}));

export const WeeklyExpandedTotalCell = styled(Box)(({ theme }) => ({
  fontFamily: 'monospace',
  fontSize: '0.85rem',
  color: theme.palette.text.secondary,
}));

// ── DetailedTab ───────────────────────────────────────────────────────────────

export const DetailedTabRoot = styled(Box)({
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

export const DetailedTableWrapper = styled(Box)({
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

export const DetailedTableInner = styled(Box)({ flex: 1, minHeight: 0 });

export const detailedTimeCellSx = { whiteSpace: 'nowrap' } as const;

export const detailedUserNameSx = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

// ── ReportFiltersBar ──────────────────────────────────────────────────────────

export const filterDescriptionInputSx = {
  fontSize: '0.82rem',
  height: 34,
  borderRadius: 1,
  mb: 0.5,
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'text.disabled' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'divider', borderWidth: 2 },
  '& .MuiInputAdornment-root': { color: 'text.disabled' },
} as const;

export const FilterArrowIcon = styled(KeyboardArrowDownIcon)({
  fontSize: 12,
  flexShrink: 0,
  opacity: 0.6,
});

export const FilterDescriptionPaper = styled(Paper)(({ theme }) => ({
  minWidth: 240,
  padding: theme.spacing(1),
}));

// ── SmartReportWidget ─────────────────────────────────────────────────────────

export const SmartWidgetTitleIcon = styled(AutoAwesomeIcon)({
  fontSize: 15,
});

export const SmartWidgetTitleText = styled(Typography)({
  fontSize: 13,
  fontWeight: 600,
  whiteSpace: 'nowrap',
});

export const SmartWidgetLoadingText = styled(Typography)({
  fontSize: 12,
});

export const SmartWidgetCountValue = styled(Typography)({
  fontSize: 13,
  fontWeight: 700,
});

export const SmartWidgetCountLabel = styled(Typography)({
  fontSize: 12,
});

export const SmartWidgetScoringText = styled(Typography)({
  fontSize: 11,
  fontStyle: 'italic',
});

export const SmartWidgetButtonIcon = styled(AutoAwesomeIcon)({
  fontSize: '14px !important',
});

// ── SummaryTab column header ──────────────────────────────────────────────────

export const ColumnHeaderSpan = styled('span')({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
});

export const expandIconStyle = { cursor: 'pointer', opacity: 0.6 } as const;

export const GroupByArrowIcon = styled(ArrowDropDownIcon)({
  fontSize: 14,
  flexShrink: 0,
  opacity: 0.6,
});

export const subGroupCountDefaultStyle = { cursor: 'default' } as const;

// ── SummaryDonutChart ─────────────────────────────────────────────────────────

export const donutTooltipLabelSx = { fontSize: 12, color: '#fff' } as const;

export const donutTooltipTimeSx = {
  fontSize: 12,
  color: '#fff',
  fontFamily: 'monospace',
  ml: 1,
} as const;

export const donutTooltipPctSx = { fontSize: 12, color: '#aaa', ml: 0.5 } as const;

export const donutLegendNameSx = {
  fontSize: 12,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

export const donutLegendPctSx = { fontSize: 12, ml: 1, flexShrink: 0 } as const;
